import React, { useState, useEffect } from 'react';
import { ExternalLink, FileText, Download, Loader2, Receipt, CreditCard, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../../components/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;

export const AdminInvoices = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/payments/invoices?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setInvoices(response.data.invoices || []);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError(err.response?.data?.detail || 'Fehler beim Laden der Rechnungen');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: { label: 'Bezahlt', class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
      open: { label: 'Offen', class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
      draft: { label: 'Entwurf', class: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' },
      void: { label: 'Storniert', class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
      uncollectible: { label: 'Uneinbringlich', class: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' }
    };
    
    const badge = badges[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency || 'EUR'
    }).format(amount);
  };

  // Loading State
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Rechnungen werden geladen...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Error State
  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">Fehler beim Laden</h3>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            <Button onClick={fetchInvoices} className="mt-4" size="sm">Erneut versuchen</Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Empty State
  if (invoices.length === 0) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4 -ml-2" data-testid="invoices-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Zurück
          </Button>
          <div className="text-center py-12">
            <Receipt className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">Noch keine Rechnungen</h3>
            <p className="text-muted-foreground mb-6">
              Sie befinden sich in der Testphase. Rechnungen werden nach Ablauf der Testphase erstellt.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Desktop Table + Mobile Cards
  return (
    <DashboardLayout>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* Header: Zurück + Titel + Abo-Status-Button */}
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-3 -ml-2"
          data-testid="invoices-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück
        </Button>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">Rechnungen & Zahlungen</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Übersicht aller Rechnungen für Ihr Abonnement
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/subscription')}
            className="flex-shrink-0 flex items-center gap-1.5 text-xs"
            data-testid="invoices-abo-status-btn"
          >
            <CreditCard className="h-3.5 w-3.5" />
            Abo Status
          </Button>
        </div>
      </div>

      {/* Desktop Table (hidden on mobile) */}
      <div className="hidden lg:block bg-card rounded-lg border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Rechnung
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Datum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Zeitraum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Betrag
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-muted/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-muted-foreground mr-2" />
                      <div>
                        <div className="text-sm font-medium">{invoice.number || invoice.id}</div>
                        <div className="text-xs text-muted-foreground">{invoice.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {formatDate(invoice.created)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                    {formatAmount(invoice.amount_paid, invoice.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(invoice.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <div className="flex items-center justify-end gap-2">
                      {invoice.hosted_invoice_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Ansehen
                        </Button>
                      )}
                      {invoice.invoice_pdf && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(invoice.invoice_pdf, '_blank')}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards (visible only on mobile) */}
      <div className="lg:hidden space-y-4">
        {invoices.map((invoice) => (
          <div key={invoice.id} className="bg-card rounded-lg border shadow-sm p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium">{invoice.number || invoice.id}</div>
                  <div className="text-xs text-muted-foreground">{invoice.description}</div>
                </div>
              </div>
              {getStatusBadge(invoice.status)}
            </div>
            
            <div className="space-y-2 mb-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Datum:</span>
                <span className="font-medium">{formatDate(invoice.created)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Zeitraum:</span>
                <span className="font-medium text-xs">
                  {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Betrag:</span>
                <span className="font-semibold text-base">{formatAmount(invoice.amount_paid, invoice.currency)}</span>
              </div>
            </div>
            
            <div className="flex gap-2">
              {invoice.hosted_invoice_url && (
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Ansehen
                </Button>
              )}
              {invoice.invoice_pdf && (
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.open(invoice.invoice_pdf, '_blank')}
                >
                  <Download className="h-4 w-4 mr-1" />
                  PDF
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
    </DashboardLayout>
  );
};
